import { type Locale } from './locales';
import { ar } from './messages/ar';
import { en } from './messages/en';
import { fr, type MessageKey } from './messages/fr';

export type { MessageKey } from './messages/fr';

export const CATALOGUES: Record<Locale, Record<MessageKey, string>> = { fr, ar, en };

export type MessageParams = Record<string, string | number>;

/**
 * `{{name}}` substitution — shared by `translate()` below and
 * `context.tsx`'s own `t()`, so the two never drift into two different
 * interpolation syntaxes. Deliberately NOT a plural-forms engine (see
 * `context.tsx`'s own header for why).
 */
export function interpolate(template: string, params: MessageParams | undefined): string {
  if (params === undefined) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Server-safe translation — no React import (unlike `useI18n().t()`,
 * `context.tsx`'s hook), so it works from a Next.js Server Component or
 * Server Action, neither of which can call a hook. Same catalogues, same
 * `{{param}}` interpolation, just without the context plumbing a Client
 * Component tree needs. The storefront's pages and most of its
 * components are deliberately Server Components (no client bundle for
 * static content) — this is what lets them stay that way while still
 * reading the real fr/ar/en catalogues.
 */
export function translate(locale: Locale, key: MessageKey, params?: MessageParams): string {
  return interpolate(CATALOGUES[locale][key], params);
}
